import React, { createContext, useCallback, useContext, useState } from 'react'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ModalFormId } from 'src/components/ModalForms'
import { useModalState } from 'src/Modal/ModalStateContext'
import { SaveArtifactAsEvidenceModal } from '../modals/SaveArtifactAsEvidenceModal'

export type SavedEvidenceInfo = {
  evidenceInstanceId: string
}

export type ArtifactModalState = {
  selectedArtifact: string | null
  setSelectedArtifact: React.Dispatch<React.SetStateAction<string | null>>
  openSaveAsEvidence: (artifact: Artifact) => void
  savedEvidenceMap: Record<string, SavedEvidenceInfo>
  markArtifactSaved: (artifactId: string, evidenceInstanceId: string) => void
}

const initialArtifactModalState: ArtifactModalState = {
  selectedArtifact: null,
  setSelectedArtifact: () => {
    return
  },
  openSaveAsEvidence: () => {
    return
  },
  savedEvidenceMap: {},
  markArtifactSaved: () => {
    return
  },
}

const ArtifactModalContext = createContext<ArtifactModalState>(
  initialArtifactModalState,
)

export const useArtifactModalContext = (): ArtifactModalState =>
  useContext(ArtifactModalContext)

export const ArtifactModalProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [selectedArtifact, setSelectedArtifact] = useState<string | null>(null)
  const [evidenceArtifact, setEvidenceArtifact] = useState<Artifact | null>(
    null,
  )
  const [savedEvidenceMap, setSavedEvidenceMap] = useState<
    Record<string, SavedEvidenceInfo>
  >({})
  const { openModal } = useModalState()

  const openSaveAsEvidence = useCallback(
    (artifact: Artifact) => {
      setEvidenceArtifact(artifact)
      openModal(ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE)
    },
    [openModal],
  )

  const markArtifactSaved = useCallback(
    (artifactId: string, evidenceInstanceId: string) => {
      setSavedEvidenceMap((prev) => ({
        ...prev,
        [artifactId]: { evidenceInstanceId },
      }))
    },
    [],
  )

  return (
    <ArtifactModalContext.Provider
      value={{
        selectedArtifact,
        setSelectedArtifact,
        openSaveAsEvidence,
        savedEvidenceMap,
        markArtifactSaved,
      }}
    >
      {children}
      {evidenceArtifact && (
        <SaveArtifactAsEvidenceModal artifact={evidenceArtifact} />
      )}
    </ArtifactModalContext.Provider>
  )
}
